import React, { ReactElement } from "react";

import { Box, Modal, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import { theme } from "@/src/theme/theme";

interface ModalWrapperProps {
  showModal?: boolean;
  onClose: () => void;
  title: string;
  children?: ReactElement | ReactElement[];
  modalBody: string;
  buttonControls: ReactElement;
  /** Full-width stacked actions (e.g. primary / outlined / text). */
  actionLayout?: "default" | "stacked";
  /** Merged into the modal paper box (padding, maxWidth, borderRadius, …). */
  paperSx?: SxProps<Theme>;
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 700,
  bgcolor: theme.palette.background.paper,
  boxShadow: 24,
  borderRadius: "2px",
  p: 2
};

const ModalWrapper = (props: ModalWrapperProps) => {
  const {
    showModal = false,
    onClose,
    title,
    modalBody,
    buttonControls,
    children,
    actionLayout = "default",
    paperSx
  } = props;
  const buttonBoxSx =
    actionLayout === "stacked"
      ? {
          display: "flex",
          flexDirection: "column" as const,
          gap: 3,
          mt: { xs: 4, sm: 5 },
          alignItems: "stretch"
        }
      : { textAlign: "right" as const };
  return (
    <Modal open={showModal} onClose={() => onClose()}>
      <Box
        sx={(paperSx ? [modalStyle, paperSx] : modalStyle) as SxProps<Theme>}
      >
        <Box>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={
              actionLayout === "stacked"
                ? {
                    fontWeight: theme.typography.fontWeightBold,
                    lineHeight: 1.3
                  }
                : undefined
            }
          >
            {title}
          </Typography>
          {modalBody ? (
            <Typography
              id="modal-modal-description"
              sx={{ mt: 2, mb: 4 }}
              dangerouslySetInnerHTML={{ __html: modalBody }}
            />
          ) : null}
          {children}
          <Box sx={buttonBoxSx}>{buttonControls}</Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalWrapper;
